const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  avatarUrl?: string;
  phone?: string;
  balance?: number;
}

export interface LoginResponse {
  success: boolean;
  data?: {
    user: AuthUser;
    accessToken: string;
    sessionId: string;
    csrfToken: string;
  };
  mfaRequired?: boolean;
  mfaToken?: string;
  error?: string;
}

export interface MFAResponse {
  success: boolean;
  data?: {
    user: AuthUser;
    accessToken: string;
    sessionId: string;
    csrfToken: string;
  };
  error?: string;
}

const STORAGE_KEYS = {
  TOKEN: 'pwa_internal_access_token',
  USER: 'pwa_internal_user',
  SESSION: 'pwa_internal_session_id',
  CSRF: 'pwa_internal_csrf_token',
  REMEMBER_EMAIL: 'pwa_internal_remembered_email',
};

export const authService = {
  getToken: (): string | null => localStorage.getItem(STORAGE_KEYS.TOKEN),
  setToken: (token: string) => localStorage.setItem(STORAGE_KEYS.TOKEN, token),
  removeToken: () => localStorage.removeItem(STORAGE_KEYS.TOKEN),

  getUser: (): AuthUser | null => {
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  },
  setUser: (user: AuthUser) => localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user)),
  removeUser: () => localStorage.removeItem(STORAGE_KEYS.USER),

  getSessionId: (): string | null => localStorage.getItem(STORAGE_KEYS.SESSION),
  setSessionId: (id: string) => localStorage.setItem(STORAGE_KEYS.SESSION, id),
  removeSessionId: () => localStorage.removeItem(STORAGE_KEYS.SESSION),

  getCsrfToken: (): string | null => localStorage.getItem(STORAGE_KEYS.CSRF),
  setCsrfToken: (token: string) => localStorage.setItem(STORAGE_KEYS.CSRF, token),
  removeCsrfToken: () => localStorage.removeItem(STORAGE_KEYS.CSRF),

  getRememberedEmail: (): string | null => localStorage.getItem(STORAGE_KEYS.REMEMBER_EMAIL),
  setRememberedEmail: (email: string) => localStorage.setItem(STORAGE_KEYS.REMEMBER_EMAIL, email),
  removeRememberedEmail: () => localStorage.removeItem(STORAGE_KEYS.REMEMBER_EMAIL),

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });

    const result = (await response.json()) as LoginResponse;

    if (!response.ok) {
      if (result.mfaRequired) {
        return {
          success: false,
          mfaRequired: true,
          mfaToken: result.mfaToken ?? result.data?.accessToken,
        };
      }
      throw new Error(result.error || 'Error al iniciar sesión');
    }

    if (result.data?.accessToken) {
      this.setToken(result.data.accessToken);
      this.setSessionId(result.data.sessionId);
      this.setCsrfToken(result.data.csrfToken);
      this.setUser(result.data.user);
    }

    return result;
  },

  async verifyMFA(mfaToken: string, code: string): Promise<MFAResponse> {
    const response = await fetch(`${API_URL}/auth/mfa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mfaToken, code }),
      credentials: 'include',
    });

    const result = (await response.json()) as MFAResponse;

    if (!response.ok) {
      throw new Error(result.error || 'Error al verificar MFA');
    }

    if (result.data?.accessToken) {
      this.setToken(result.data.accessToken);
      this.setSessionId(result.data.sessionId);
      this.setCsrfToken(result.data.csrfToken);
      this.setUser(result.data.user);
    }

    return result;
  },

  async verifyToken(): Promise<AuthUser | null> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Session-Id': this.getSessionId() ?? '',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.clearSession();
        }
        return null;
      }

      const result = await response.json();
      if (result.data) {
        this.setUser(result.data);
        return result.data as AuthUser;
      }
      return null;
    } catch (error) {
      console.error('Error verificando token:', error);
      return null;
    }
  },

  async logout(): Promise<void> {
    const token = this.getToken();
    const sessionId = this.getSessionId();

    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token ?? ''}`,
          'X-Session-Id': sessionId ?? '',
        },
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error en logout:', error);
    }

    this.clearSession();
  },

  clearSession(): void {
    this.removeToken();
    this.removeUser();
    this.removeSessionId();
    this.removeCsrfToken();
  },

  fetchWithAuth: async (url: string, options: RequestInit = {}): Promise<any> => {
    const token = authService.getToken();
    const sessionId = authService.getSessionId();
    const csrfToken = authService.getCsrfToken();

    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    };

    if (options.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }

    const response = await fetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        authService.clearSession();
      }
      const errorBody = await response.json().catch(() => ({ error: 'Error en la petición' }));
      throw new Error(errorBody.error || `Error ${response.status}: ${response.statusText}`);
    }

    return response.json();
  },
};